import { memo, useCallback, useMemo, useState } from "react"
import { format } from "date-fns"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { PopoverProps } from "@productlane/ui"
import type { Feedback } from "@productlane/zero"
import { cn } from "@productlane/lib/src/browser"
import {
	Button,
	Popover,
	SlaBreachIcon,
	SlaIcon,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"

import { RoleCheck } from "@/components/shared/role-check"
import { getSlaSuggestionsForInput } from "@/lib/dates"
import { useTimeTick } from "@/lib/hooks/use-time-tick"
import {
	computeSlaDeadline,
	formatTimeRemaining,
	SLA_DURATIONS,
	SLA_STATUS_DEFINITIONS,
	SLA_STATUS_LABELS,
} from "@/lib/sla-utils"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { useSlaFeatureEnabled } from "./hooks/use-sla-feature-enabled"

interface SlaBadgeProps {
	thread: Pick<
		Feedback,
		| "id"
		| "slaDeadlineAt"
		| "state"
		| "lastStateChangeAt"
		| "replied"
		| "lastInboundMessageAt"
		| "lastOutboundMessageAt"
	>
	modal?: PopoverProps["modal"]
}

export const SlaBadge = memo(({ thread, modal }: SlaBadgeProps) => {
	const zero = useZero()
	const { role } = useRole()
	const slaEnabled = useSlaFeatureEnabled()
	const [open, onOpenChange] = useToggle(false)

	const tick = useTimeTick()
	const slaInfo = useMemo(
		() => computeSlaDeadline(thread),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[thread, tick],
	)

	const [query, setQuery] = useState("")

	const suggestions = useMemo(() => getSlaSuggestionsForInput(query), [query])

	const handleSetDeadline = useCallback(
		async (date: Date) => {
			await mutateOk(
				zero.mutate(
					mutators.threads.update({
						id: thread.id,
						slaDeadlineAtIso: date.toISOString(),
					}),
				),
			)
			setQuery("")
			onOpenChange(false)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[zero.mutate, thread.id, onOpenChange],
	)

	const handleSetPreset = useCallback(
		async (minutes: number) => {
			const deadline = new Date(Date.now() + minutes * 60 * 1000)
			await handleSetDeadline(deadline)
		},
		[handleSetDeadline],
	)

	const handleRemove = useCallback(async () => {
		// Set to epoch (0) as sentinel to mean "explicitly removed"
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					slaDeadlineAtIso: new Date(0).toISOString(),
				}),
			),
		)
		setQuery("")
		onOpenChange(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zero.mutate, thread.id, onOpenChange])

	if (!slaEnabled) return null

	// No active SLA — removed, never set, or paused because the team has replied
	if (!slaInfo) return null

	const status = slaInfo?.status
	const isBreached = status === "breached"
	const isFailed = status === "failed"
	const isAchieved = status === "achieved"
	const isHighRisk = status === "highRisk"
	const isMediumRisk = status === "mediumRisk"
	const timeText = slaInfo ? formatTimeRemaining(slaInfo.deadline) : null

	const label = isBreached
		? `Breached by ${timeText}`
		: isFailed
			? `Failed by ${timeText}`
			: isAchieved
				? "Achieved"
				: timeText
					? `${timeText} remaining`
					: "SLA"

	const tooltipContent = slaInfo ? (
		<div className="flex items-start gap-2">
			<SlaIcon
				className={cn(
					"mt-0.5 h-4 w-4 shrink-0",
					isBreached || isFailed
						? "text-red-500 dark:text-red-400"
						: isAchieved
							? "text-green-500 dark:text-green-400"
							: isHighRisk
								? "text-red-500 dark:text-red-400"
								: isMediumRisk
									? "text-yellow-600 dark:text-yellow-400"
									: "text-orange-500 dark:text-orange-400",
				)}
			/>
			<div className="mt-0.5 flex flex-col gap-0.5">
				<span className="font-medium">{SLA_STATUS_LABELS[slaInfo.status]}</span>
				<span className="text-tertiary">
					{SLA_STATUS_DEFINITIONS[slaInfo.status]}
				</span>
				<span className="text-tertiary">
					{isAchieved || isFailed || isBreached
						? `Was due on ${format(slaInfo.deadline, "EEE, d MMM, HH:mm")}`
						: `Due on ${format(slaInfo.deadline, "EEE, d MMM, HH:mm")}`}
				</span>
			</div>
		</div>
	) : null

	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			modal={modal}
			className="p-0"
			content={
				<RoleCheck not={["VIEWER"]}>
					<Command shouldFilter={false}>
						<CommandInput
							autoFocus
							placeholder="Change SLA..."
							value={query}
							onValueChange={setQuery}
						/>
						<CommandEmpty>No matching option</CommandEmpty>
						<CommandList>
							<CommandGroup>
								{!query && (thread.slaDeadlineAt || slaInfo) && (
									<CommandItem onSelect={handleRemove}>
										<div className="flex items-center gap-2">
											<X className="h-4 w-4" />
											<span>Remove SLA</span>
										</div>
									</CommandItem>
								)}
								{!query && suggestions.length === 0
									? SLA_DURATIONS.map((opt) => (
											<CommandItem
												key={opt.minutes}
												onSelect={() => handleSetPreset(opt.minutes)}
											>
												<div className="flex w-full items-center justify-between">
													<span>{opt.label}</span>
													<span className="text-muted-foreground text-xs">
														{format(
															new Date(Date.now() + opt.minutes * 60 * 1000),
															"EEE, d MMM, HH:mm",
														)}
													</span>
												</div>
											</CommandItem>
										))
									: suggestions.map((suggestion) => (
											<CommandItem
												key={suggestion.label}
												value={suggestion.label}
												onSelect={() => handleSetDeadline(suggestion.date)}
											>
												<div className="flex w-full items-center justify-between">
													<span>{suggestion.label}</span>
													<span className="text-muted-foreground text-xs">
														{format(suggestion.date, "EEE, d MMM, HH:mm")}
													</span>
												</div>
											</CommandItem>
										))}
							</CommandGroup>
						</CommandList>
					</Command>
				</RoleCheck>
			}
		>
			<Tooltip label={tooltipContent} enabled={!!tooltipContent}>
				<Button
					variant="ghost"
					Icon={() =>
						isBreached || isFailed ? (
							<SlaBreachIcon className="h-4 w-4 text-red-500 dark:text-red-400" />
						) : (
							<SlaIcon
								className={cn(
									"h-4 w-4",
									isAchieved
										? "text-green-500 dark:text-green-400"
										: isHighRisk
											? "text-red-500 dark:text-red-400"
											: isMediumRisk
												? "text-yellow-600 dark:text-yellow-400"
												: "text-orange-500 dark:text-orange-400",
								)}
							/>
						)
					}
					className={cn(
						"h-auto min-w-[145px] justify-start text-xs",
						isBreached || isFailed
							? "!text-red-500 dark:!text-red-400"
							: isAchieved
								? "!text-green-600 dark:!text-green-400"
								: isHighRisk
									? "!text-red-500 dark:!text-red-400"
									: isMediumRisk
										? "!text-yellow-600 dark:!text-yellow-400"
										: "!text-primary",
						role === "VIEWER" && "pointer-events-none",
					)}
				>
					{label}
				</Button>
			</Tooltip>
		</Popover>
	)
})
